import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FaLock, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import API_URL from '../../config/api';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('reset-password');

  const validationSchema = Yup.object({
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        'Password must contain at least one uppercase letter, one lowercase letter, and one number'
      )
      .required('Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], 'Passwords must match')
      .required('Confirm password is required')
  });

  const resetPassword = async (password) => {
    if (!token) {
      throw new Error('Invalid reset token');
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      throw new Error(error.message || 'Failed to reset password. Please try again.');
    }
  };

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        await resetPassword(values.password);
        setCurrentView('success');
      } catch (error) {
        formik.setStatus(error.message);
      } finally {
        setLoading(false);
      }
    }
  });

  const handleBackToLogin = () => {
    navigate('/signin');
  };

  // Invalid token view
  if (!token && currentView === 'reset-password') {
    return (
      <div className="flex min-h-screen">
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[440px]">
            <div className="mb-12">
              <div className="mb-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-8">
                  <FaExclamationTriangle className="text-red-500 text-3xl" />
                </div>
                <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                  Invalid Reset Link
                </h2>
                <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                  This password reset link is invalid or has expired.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="p-6 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                      1
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                        Request New Link
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        Go back to the forgot password page to request a new reset link
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-black text-white flex items-center justify-center text-xs font-normal">
                      2
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                        Check Your Email
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        Make sure to use the latest reset link sent to your email
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <Link 
                    to="/forgot-password"
                    className="block w-full bg-black text-white px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-700"
                  >
                    Request New Reset Link
                  </Link>
                  
                  <button 
                    onClick={handleBackToLogin}
                    className="block w-full border border-gray-300 bg-white text-gray-700 px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-50"
                  >
                    Back to Sign In
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Reset password form view
  if (currentView === 'reset-password') {
    return (
      <div className="flex min-h-screen">
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[440px]">
            <div className="mb-12">
              <div className="mb-12">
                <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                  Create New Password
                </h2>
                <p className="text-sm text-gray-600 font-normal tracking-wide">
                  Enter your new password below
                </p>
              </div>
              
              {formik.status && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm">
                  {formik.status}
                </div>
              )}
              
              <form className="space-y-8" onSubmit={formik.handleSubmit}>
                <div>
                  <label htmlFor="password" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    New Password
                  </label>
                  <input 
                    type="password" 
                    id="password"
                    name="password"
                    required 
                    placeholder="Enter new password"
                    className={`w-full px-4 py-3 border ${formik.touched.password && formik.errors.password ? 'border-red-300' : 'border-gray-200'} focus:border-gray-300 focus:outline-none transition-colors text-sm font-normal placeholder:text-gray-400`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    autoComplete="new-password"
                  />
                  {formik.touched.password && formik.errors.password ? (
                    <div className="text-red-600 text-xs mt-1">{formik.errors.password}</div>
                  ) : (
                    <div className="text-gray-400 text-xs mt-1">
                      Must be at least 8 characters with uppercase, lowercase, and number
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-[10px] uppercase tracking-[0.1em] text-gray-500 mb-3 font-medium">
                    Confirm Password
                  </label>
                  <input 
                    type="password" 
                    id="confirmPassword"
                    name="confirmPassword"
                    required 
                    placeholder="Confirm new password"
                    className={`w-full px-4 py-3 border ${formik.touched.confirmPassword && formik.errors.confirmPassword ? 'border-red-300' : 'border-gray-200'} focus:border-gray-300 focus:outline-none transition-colors text-sm font-normal placeholder:text-gray-400`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.confirmPassword}
                    autoComplete="new-password"
                  />
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <div className="text-red-600 text-xs mt-1">{formik.errors.confirmPassword}</div>
                  )}
                </div>

                <div className="pt-8">
                  <button 
                    type="submit"
                    disabled={loading || !formik.isValid || !formik.dirty} 
                    className="w-full bg-black text-white py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Resetting...
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </div>
              </form>

              <div className="mt-12 text-center">
                <p className="text-sm text-gray-600 font-normal tracking-wide">
                  Remember your password?
                  <button 
                    onClick={handleBackToLogin}
                    className="text-gallery-dark hover:text-gray-600 transition-colors font-normal ml-1 underline"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success view
  if (currentView === 'success') {
    return (
      <div className="flex min-h-screen">
        <div className="flex-1 flex items-center justify-center px-6 py-12 bg-white">
          <div className="w-full max-w-[440px]">
            <div className="mb-12">
              <div className="mb-12 text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-8">
                  <FaCheck className="text-green-600 text-3xl" />
                </div>
                <h2 className="text-sm font-medium tracking-[0.2em] uppercase mb-2">
                  Password Reset Successful!
                </h2>
                <p className="text-sm text-gray-600 font-normal tracking-wide leading-relaxed max-w-md mx-auto">
                  Your password has been successfully reset. You can now sign in with your new password.
                </p>
              </div>
              
              <div className="space-y-6">
                <div className="p-6 border border-gray-200">
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="flex-shrink-0">
  <FaLock className="text-black text-lg mt-1" />
</div>

                    <div>
                      <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                        Secure Password Updated
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        Your account is now protected with your new password. Make sure to keep it safe.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
  <FaCheck className="text-black text-xs" />
</div>

                    </div>
                    <div>
                      <p className="text-sm font-medium text-gallery-dark mb-2 tracking-wide">
                        Ready to Sign In
                      </p>
                      <p className="text-xs text-gray-600 font-normal leading-relaxed tracking-wide">
                        You Can sign in now using your new password
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <button 
                  onClick={() => navigate('/signin')}
                  className="block w-full bg-black text-white px-8 py-3 transition-all duration-300 text-xs tracking-[0.2em] font-normal uppercase text-center hover:bg-gray-700"
                >
                  Sign In 
                </button>  
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default ResetPassword;